import { useMemo } from 'react';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { CoachOverallGradeBadge } from '@/components/coaches/coach-overall-grade-badge';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { Badge } from '@/components/ui/badge';
import { getCoach, getCoachTqReviews, getUser } from '@/lib/api-calls';
import { getCoachOverallGradeColor } from '@/lib/coach-utils';
import { airServerErrorFrom, axiosErrorFrom, useErrorToast } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';
import { validateRouteRole } from '@/lib/routing';
import { airColumnHelper } from '@/lib/tables';
import { cn } from '@/lib/tailwind-utils';
import { CoachGrade, formatInstant, isAirAdmin } from '@airreading/common';
import {
  CoachAttendanceGrade,
  coachOverallGradeFrom,
  CoachTqReview,
  CoachTqReviewStatus,
  CoachTqReviewStatusLabelMap,
  CoachTqReviewTypeLabelMap,
} from '@airreading/common/data';
import { AssessmentRepeatReviewAction } from '../-components/assessment-repeat-review-action';
import { ManagePermissionsDropdown } from '../-components/manage-permissions-dropdown';
import { coachesTabs } from '../-components/tabs';
import { CoachTqReviewTypeBadgeClassMap } from '../-components/tq-review-type-badge';

export const Route = createFileRoute('/_layout/teachers/$teacherId/review-notes')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: ReviewNotesPage,
});

const coachTqReviewTableColumnSchema = z.enum(['reviewDate', 'status', 'reviewType', 'action']);
const col = airColumnHelper<CoachTqReview>(coachTqReviewTableColumnSchema);

function LetterGradeBadge({ grade }: { grade: CoachGrade | CoachAttendanceGrade | null | undefined }) {
  if (!grade) {
    return <span className="text-sm text-muted-foreground">-</span>;
  }

  const color = getCoachOverallGradeColor(coachOverallGradeFrom(grade));
  if (!color) {
    return <span className="text-sm text-muted-foreground">{grade.replaceAll('_', ' ')}</span>;
  }

  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white font-bold w-7 h-7 text-xl shrink-0"
      style={{ backgroundColor: color }}
    >
      {grade}
    </span>
  );
}

function ReviewNotesPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load review notes. Please try again.',
        };
      }}
    >
      <ReviewNotesDetail />
    </InfoPageSuspenseBoundary>
  );
}

function ReviewNotesDetail() {
  const { teacherId: userId } = Route.useParams();
  const timeZone = Temporal.Now.timeZoneId();

  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(userId),
    queryFn: () => getUser(userId),
  });
  const { data: coach } = useSuspenseQuery(getCoach(userId));
  const { data: reviews = [], error, isPending, refetch } = useQuery(getCoachTqReviews(userId));
  useErrorToast(error, 'Error loading review history. Please try again.');

  const lastUpdatedLabel = coach.qualityGradeUpdatedAt
    ? `Last updated at ${formatInstant(Temporal.Instant.from(coach.qualityGradeUpdatedAt), timeZone).formattedDate}`
    : 'Last updated at —';

  const columns = useMemo(
    () => [
      col.accessor('reviewDate', {
        id: 'reviewDate',
        header: 'Review date',
        cell: ({ getValue }) => {
          const reviewDate = getValue();
          return reviewDate ? formatInstant(Temporal.Instant.from(reviewDate), timeZone).formattedDate : '—';
        },
        enableSorting: false,
      }),
      col.accessor('status', {
        id: 'status',
        header: 'Review status',
        cell: ({ getValue }) => {
          const status = getValue();
          return status === CoachTqReviewStatus.blank ? '—' : CoachTqReviewStatusLabelMap[status];
        },
        enableSorting: false,
      }),
      col.accessor('reviewType', {
        id: 'reviewType',
        header: 'Review type',
        cell: ({ getValue }) => {
          const reviewType = getValue();
          return (
            <Badge
              variant="outline"
              className={cn('rounded-full font-medium', CoachTqReviewTypeBadgeClassMap[reviewType])}
            >
              {CoachTqReviewTypeLabelMap[reviewType]}
            </Badge>
          );
        },
        enableSorting: false,
      }),
      col.display({
        id: 'action',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => <AssessmentRepeatReviewAction coachId={userId} review={row.original} />,
      }),
    ],
    [timeZone, userId],
  );

  const table = useReactTable({
    data: reviews,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className="mb-2">
      <InfoPage
        headerText={user.fullNameOrEmail}
        headerActions={<ManagePermissionsDropdown user={user} />}
        tabs={coachesTabs({ teacherId: userId })}
        breadcrumbs={{
          linkItems: [
            {
              label: 'Teachers',
              link: {
                to: '/teachers',
              },
            },
          ],
          currentPage: user.fullNameOrEmail ?? '',
        }}
      >
        <div className="rounded-lg border bg-white p-6 shadow-sm space-y-8">
          <h2 className="text-2xl font-bold">Review notes</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div className="space-y-2">
              <p className="text-sm font-semibold leading-5 text-black">Overall grade</p>
              <CoachOverallGradeBadge grade={coach.overallGrade} />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold leading-5 text-black">Teaching grade</p>
              <LetterGradeBadge grade={coach.qualityGrade} />
              <p className="text-xs text-muted-foreground pt-1">{lastUpdatedLabel}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold leading-5 text-black">Attendance grade</p>
              <LetterGradeBadge grade={coach.attendanceGrade} />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold leading-5 text-black">Historical review notes</p>
            <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-muted/30 p-4 text-sm text-foreground whitespace-pre-wrap">
              {coach.historicalReviewNotes?.trim() || '—'}
            </div>
          </div>

          <EntityTable
            table={table}
            showPagination={false}
            hideEndMessage
            isLoading={isPending}
            hasError={!!error}
            emptyState="No completed reviews yet."
            errorState={<TableErrorState refetchAction={refetch} />}
          />
        </div>
      </InfoPage>
    </div>
  );
}
